<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.stable_edit") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Stable Avatar -->
          <base-image-upload-input
            col="12"
            identifier="avatar"
            :placeholder="$t('PLACEHOLDERS.stable_image')"
            @selectImage="selectStableImage"
            :preSelectedImage="data.avatar.path"
          />
          <!-- End:: Stable Avatar -->

          <!-- Start:: Additional Images -->
          <v-col cols="12">
            <UploadsPhotos
              :urls="imgUrls"
              multiple
              @onFileSelect="onFileSelect"
              @onFileRemove="onFileRemove"
            >
              {{ $t("PLACEHOLDERS.stable_images") }}
            </UploadsPhotos>
          </v-col>
          <!-- End:: Additional Images -->

          <!-- Start:: Basic Information -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.stable_name')"
            v-model.trim="data.name"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model="data.mobile"
            required
          />

          <base-input
            col="6"
            type="email"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.commercial_registration_number')"
            v-model.trim="data.commercial_registration_number"
          />

          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.description')"
            v-model.trim="data.description"
          />
          <!-- End:: Basic Information -->

          <!-- Start:: Location Information -->
          <hr style="width: 98%" class="my-5" />
          <h5 class="font-weight-bold" style="color: #F7941E">
            {{ $t("PLACEHOLDERS.address") }}
          </h5>

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model.trim="data.city"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.district')"
            v-model.trim="data.district"
          />

          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.address')"
            v-model.trim="data.address"
          />

          <p class="mb-5 px-3" style="color: #F7941E; font-size: 16px">
            {{ $t("PLACEHOLDERS.location_on_map") }}
            <span class="text-danger"> * </span>
          </p>
          <ChangableMapComponent
            :lat="parseFloat(data.latitude)"
            :lng="parseFloat(data.longitude)"
            @update:lat="data.latitude = $event"
            @update:lng="data.longitude = $event"
            @update:placeName="data.location = $event"
          />
          <!-- End:: Location Information -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import UploadsPhotos from "@/components/formInputs/UploadsPhotos.vue";
import ChangableMapComponent from "@/components/general/ChangableMapComponent";

export default {
  name: "EditStable",
  components: { UploadsPhotos, ChangableMapComponent },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        avatar: {
          path: null,
          file: null,
        },
        name: "",
        mobile: "",
        email: "",
        commercial_registration_number: "",
        description: "",
        latitude: 24.7136,
        longitude: 46.6753,
        city: "",
        district: "",
        address: "",
        location: "",
        existing_images: [],
      },
      imgUrls: [],
      additionalImages: [],
      imagesToDelete: [],
      // End:: Data Collection To Send
    };
  },

  methods: {
    selectStableImage(selectedImage) {
      this.data.avatar = selectedImage;
    },

    onFileSelect(files) {
      this.additionalImages = files;
    },

    onFileRemove(index) {
      // If it's an existing image, add to delete list
      if (this.imgUrls[index] && this.imgUrls[index].id) {
        this.imagesToDelete.push(this.imgUrls[index].id);
      }
      
      this.$delete(this.imgUrls, index);
      
      // If it's a new image, remove from additionalImages
      if (this.additionalImages[index]) {
        this.$delete(this.additionalImages, index);
      }
    },

    // Start:: validate Form Inputs
    validateFormInputs() {
      if (!this.data.name || !this.data.mobile || !this.data.email) {
        this.$message.error(this.$t("MESSAGES.fillRequiredFields"));
        return;
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();

      // Append avatar if new file selected
      if (this.data.avatar.file) {
        REQUEST_DATA.append("avatar", this.data.avatar.file);
      }

      // Append new additional images
      if (this.additionalImages && this.additionalImages.length > 0) {
        this.additionalImages.forEach((image, index) => {
          REQUEST_DATA.append(`images[${index}]`, image);
        });
      }

      // Append images to delete
      if (this.imagesToDelete.length > 0) {
        this.imagesToDelete.forEach((imageId, index) => {
          REQUEST_DATA.append(`delete_images[${index}]`, imageId);
        });
      }

      // Append basic data
      REQUEST_DATA.append("name", this.data.name);
      REQUEST_DATA.append("mobile", this.data.mobile);
      REQUEST_DATA.append("email", this.data.email);
      
      if (this.data.commercial_registration_number) {
        REQUEST_DATA.append("commercial_registration_number", this.data.commercial_registration_number);
      }
      
      if (this.data.description) {
        REQUEST_DATA.append("description", this.data.description);
      }

      // Append location data
      REQUEST_DATA.append("latitude", this.data.latitude);
      REQUEST_DATA.append("longitude", this.data.longitude);
      REQUEST_DATA.append("city", this.data.city);
      
      if (this.data.district) {
        REQUEST_DATA.append("district", this.data.district);
      }
      
      if (this.data.address) {
        REQUEST_DATA.append("address", this.data.address);
      }
      
      if (this.data.location) {
        REQUEST_DATA.append("location", this.data.location);
      }

      REQUEST_DATA.append("_method", "PUT");

      try {
        await this.$axios({
          method: "POST",
          url: `stables/${this.$route.params.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.updatedSuccessfully"));
        this.$router.push({ path: "/stables/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        if (error.response && error.response.data && error.response.data.message) {
          this.$message.error(error.response.data.message);
        } else {
          this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
        }
      }
    },
    // End:: Submit Form

    // Start:: Get Stable Data
    async getStableData() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `stables/${this.$route.params.id}`,
        });
        
        const stableData = res.data.data;
        
        this.data.name = stableData.name;
        this.data.mobile = stableData.mobile;
        this.data.email = stableData.email;
        this.data.commercial_registration_number = stableData.commercial_registration_number;
        this.data.description = stableData.description;
        this.data.latitude = stableData.latitude || 24.7136;
        this.data.longitude = stableData.longitude || 46.6753;
        this.data.city = stableData.city;
        this.data.district = stableData.district;
        this.data.address = stableData.address;
        this.data.location = stableData.location;
        
        // Set avatar
        if (stableData.avatar) {
          this.data.avatar.path = stableData.avatar;
        }
        
        // Set existing images
        if (stableData.images && stableData.images.length > 0) {
          this.imgUrls = stableData.images.map((image, index) => ({
            id: image.id || index,
            url: image.url || image,
            name: `Image ${index + 1}`
          }));
          this.data.existing_images = [...this.imgUrls];
        }
        
      } catch (error) {
        console.log(error.response?.data?.message || "Error loading stable data");
        this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
        this.$router.push({ path: "/stables/all" });
      }
    },
    // End:: Get Stable Data
  },

  created() {
    // Start:: Fire Methods
    this.getStableData();
    // End:: Fire Methods
  },
};
</script>

<style>
.preview-container img {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #ccc;
  border-radius: 8px;
}
</style>
